import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { Actor, ActorMap, Client, ClientMap, ClientProfile } from '../../types';
import { RootState } from '../store';

interface ClientState {
  actors: ActorMap;
  clients: ClientMap;
  currentClientId?: string;
  clientProfile: ClientProfile;
}

const initialState: ClientState = {
  actors: {},
  clients: {},
  clientProfile: {
    age: null,
    gender: null,
    address: null,
    ethnicity: null,
    language_preferred: null,
    ses: null,
    religion: null,
    occupation: null,
    accommodation: null,
    relationship_status: null,
    id: '',
    created_at: '',
    updated_at: '',
    summary: '',
    level: '',
  },
};

export const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    setClients: (state, action: PayloadAction<Client[]>) => {
      const clients: ClientMap = {};
      for (const client of action.payload) {
        clients[client.userId] = client;
      }
      state.clients = clients;
    },
    setActors: (state, action: PayloadAction<Actor[]>) => {
      const actors: ActorMap = {};
      for (const actor of action.payload) {
        actors[actor.userId] = actor;
      }
      state.actors = actors;
    },
    setClient: (state, action: PayloadAction<Client>) => {
      state.clients[action.payload.userId] = action.payload;
    },
    setClientProfile: (state, action: PayloadAction<ClientProfile>) => {
      state.clientProfile = action.payload;
    },
    delClient: (state, action: PayloadAction<string>) => {
      delete state.clients[action.payload];
    },
    setCurrentClient: (state, action: PayloadAction<string | undefined>) => {
      state.currentClientId = action.payload;
    },
    addActor: (state, action: PayloadAction<Actor>) => {
      const actor = action.payload;
      state.actors[actor.userId] = actor;
    },
    delActor: (state, action: PayloadAction<string>) => {
      delete state.actors[action.payload];
    },
  },
});

export const {
  setClients,
  setActors,
  setClient,
  setClientProfile,
  delClient,
  setCurrentClient,
  addActor,
  delActor,
} = clientSlice.actions;

export const selectActors = (state: RootState) => state.client.actors;

export const selectClients = (state: RootState) => state.client.clients;

export const selectActiveClients = createSelector(selectClients, (clientMap) =>
  Array.from(Object.values(clientMap)).filter((c) => !c.isArchived)
);

export const selectArchivedClients = createSelector(
  selectClients,
  (clientMap) =>
    Array.from(Object.values(clientMap)).filter((c) => c.isArchived)
);

export const selectCurrentClient = (state: RootState) => {
  const clientId = state.client.currentClientId;
  if (clientId) {
    if (clientId in state.client.actors) {
      return state.client.actors[clientId];
    }
    if (clientId in state.client.clients) {
      return state.client.clients[clientId];
    }
  }
};

export const selectCurrentActor = (state: RootState) => {
  const currentClientId = state.client.currentClientId;
  if (currentClientId) {
    // Fresh session: currentClientId is a plain actor ID
    if (currentClientId in state.client.actors) {
      return state.client.actors[currentClientId];
    }
    // DB-restored actor chat: use actorId stored in the client object
    const client = state.client.clients[currentClientId];
    if (client?.actorId && client.actorId in state.client.actors) {
      return state.client.actors[client.actorId];
    }
    // Legacy fallback: composite ID (actorId_username) for rows without actorId set
    const actorId = Object.keys(state.client.actors)
      .sort((a, b) => b.length - a.length)
      .find((id) => currentClientId.startsWith(id + '_'));
    if (actorId) {
      return state.client.actors[actorId];
    }
  }
};

export const selectCurrentClientId = (state: RootState) =>
  state.client.currentClientId;

export const selectClientProfile = (state: RootState) =>
  state.client.clientProfile;

export default clientSlice.reducer;
